/**
 * GlobalState.drafts 切片与 Worker 数据源接线（任务 14.12）。
 *
 * 草稿的事实源在 Worker 账号库 kv（local-state.ts，`draft/<ConversationKey>`），
 * 本切片是主线程镜像，供两处只读消费：
 * - 会话列表（ConversationList）：[草稿] 装饰 + bump 排序（conversationRowModel）；
 * - 输入栏（Composer）：打开会话时恢复初值。
 *
 * 写路径与 conversations 不同——草稿只有主线程自己会改（输入产生），所以没有
 * 推送通道：saveDraft 先乐观改切片（列表装饰即时可见），再 fire-and-forget 调
 * 'chat/draft/save' 落 kv。登录时整批快照一次（纯 IndexedDB 零 HTTP）。
 *
 * 边界：
 * - 快照在途期间的本地写不丢——快照回包以 {...snap, ...现值} 合并，现值优先
 *   （实际窗口毫秒级：快照发生在登录决议时，输入 UI 尚未存在）；
 * - 登出清空 + epoch 使在途快照过期（conversationsState 同口径）；
 * - 删会话时 Worker cleanup 连带删 kv 草稿，切片里的残留项因行已消失不再被
 *   消费，下次快照自然对齐，无需找平。
 */

import type { GlobalState, GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import type { ConversationKey } from '~/worker/contract';

export interface DraftEntry {
  readonly text: string;
  /** 毫秒时间戳（主线程盖章），列表 bump 排序用 */
  readonly updatedAt: number;
}

/** null = 未加载（未登录/快照未回包）。键 = ConversationKey */
export type DraftsSlice = Readonly<Record<string, DraftEntry>> | null;

export const DRAFTS_INITIAL: DraftsSlice = null;

/** 单条写入纯函数：空文本即清；无变化返回原引用 */
export function applyDraftSave(
  current: DraftsSlice,
  key: string,
  text: string,
  updatedAt: number,
): DraftsSlice {
  const byKey = current ?? {};
  if (text === '') {
    if (!(key in byKey)) return current;
    const next = { ...byKey };
    delete next[key];
    return next;
  }
  return { ...byKey, [key]: { text, updatedAt } };
}

export interface DraftsConnection {
  /**
   * 草稿写入（Composer 防抖后调）：text 按 trim 判空——空即清（旧版口径），
   * 非空存原文（保留首尾空白，恢复时不改用户输入）。
   */
  saveDraft(key: string, text: string): void;
  /** Worker 崩溃重建后重拉快照（kv 是事实源，死区内的写已乐观在切片里，合并保住） */
  resync(): void;
  dispose(): void;
}

export function connectDrafts(store: GlobalStore, host: AppWorkerHost): DraftsConnection {
  let epoch = 0;
  let loadedUid: number | null = null;

  function load(): void {
    const seq = ++epoch;
    void host.call('chat/drafts', null).then(
      (snap) => {
        if (seq !== epoch) return;
        store.set((prev) => {
          const fromKv: Record<string, DraftEntry> = {};
          for (const r of snap.rows) fromKv[r.key] = { text: r.text, updatedAt: r.updatedAt };
          // 现值优先：快照在途期间的乐观写不被旧快照覆盖（文件头边界 1）
          return { ...prev, drafts: { ...fromKv, ...(prev.drafts ?? {}) } };
        });
      },
      () => undefined, // Worker 初始化失败：保持现值，崩溃重启后 resync 再拉
    );
  }

  function onAuth(auth: GlobalState['auth']): void {
    if (auth.status === 'logged-in') {
      if (auth.userId !== loadedUid) {
        loadedUid = auth.userId;
        load();
      }
    } else if (auth.status === 'logged-out') {
      loadedUid = null;
      epoch += 1;
      store.set({ drafts: DRAFTS_INITIAL });
    }
  }
  const offAuth = store.subscribe((s) => s.auth, onAuth);
  onAuth(store.get().auth);

  return {
    saveDraft(key, text) {
      const normalized = text.trim() === '' ? '' : text;
      const updatedAt = Date.now();
      store.set((prev) => {
        const next = applyDraftSave(prev.drafts, key, normalized, updatedAt);
        return next === prev.drafts ? prev : { ...prev, drafts: next };
      });
      void host
        .call('chat/draft/save', { key: key as ConversationKey, text: normalized, updatedAt })
        .catch(() => undefined); // 落库失败只损失一份草稿（切片仍有，刷新即逝）
    },
    resync() {
      if (loadedUid !== null) load();
    },
    dispose() {
      offAuth();
    },
  };
}
